import { useEffect, useMemo, useState } from "react";

/**
 * Dependency-free CSS confetti burst. Renders ~48 pieces that fall from a
 * random x position with drift + rotation, then self-unmounts after
 * `duration`. Palette matches the JobGrid editorial tones.
 */
export default function ConfettiBurst({ show, onDone, duration = 2600, pieces = 48 }) {
  const [mounted, setMounted] = useState(show);

  useEffect(() => {
    if (!show) return;
    setMounted(true);
    const t = setTimeout(() => {
      setMounted(false);
      onDone?.();
    }, duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  const items = useMemo(() => {
    if (!mounted) return [];
    const palette = ["#047857", "#F5B301", "#B91C1C", "#1A1A1A", "#0369A1", "#DB2777"];
    return Array.from({ length: pieces }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 250,
      duration: 1600 + Math.random() * 1400,
      drift: (Math.random() * 200) - 100,
      rot: Math.random() * 720 - 360,
      size: 6 + Math.random() * 6,
      color: palette[i % palette.length],
      round: Math.random() < 0.35,
    }));
  }, [mounted, pieces]);

  if (!mounted) return null;
  return (
    <div data-testid="confetti-burst" className="pointer-events-none fixed inset-0 z-[100] overflow-hidden" aria-hidden="true">
      {items.map((p) => (
        <span key={p.id} className="absolute top-[-24px] block"
          style={{
            left: `${p.left}%`, width: p.size, height: p.size * (p.round ? 1 : 1.6),
            background: p.color, borderRadius: p.round ? "9999px" : "1px",
            transform: "translate3d(0,0,0)",
            animation: `cfFall ${p.duration}ms cubic-bezier(.25,.5,.35,1) ${p.delay}ms forwards`,
            "--cf-drift": `${p.drift}px`, "--cf-rot": `${p.rot}deg`,
          }} />
      ))}
      <style>{`
        @keyframes cfFall {
          0%   { opacity: 0; transform: translate3d(0,-20px,0) rotate(0deg); }
          10%  { opacity: 1; }
          100% { opacity: 0; transform: translate3d(var(--cf-drift),105vh,0) rotate(var(--cf-rot)); }
        }
      `}</style>
    </div>
  );
}
